import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Download, Search, Pencil, Trash2, Paperclip, ChevronLeft, ChevronRight, FilterX, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, downloadCsv, cx, fileUrl } from "@/lib/api";
import { eur, fmtDate, todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import TransactionModal from "@/components/TransactionModal";
import { ImportXmlButton } from "@/components/ImportXmlButton";
import FatturaPreviewModal from "@/components/FatturaPreviewModal";

const isXmlAttachment = (t) => /xml|pkcs7/.test(t.allegato_content_type || "");

const PAGE_SIZE = 12;
const INIT_FILTERS = { tipo: "all", categoria_key: "all", cantiere_id: "all", fornitore_id: "all", pagata: "all", date_from: "", date_to: "", search: "" };

export default function Transazioni() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(INIT_FILTERS);
  const [categories, setCategories] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [previewTx, setPreviewTx] = useState(null);
  const [loading, setLoading] = useState(true);

  const buildParams = useCallback(() => {
    const p = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (filters.tipo !== "all") p.tipo = filters.tipo;
    if (filters.categoria_key !== "all") p.categoria_key = filters.categoria_key;
    if (filters.cantiere_id !== "all") p.cantiere_id = filters.cantiere_id;
    if (filters.fornitore_id !== "all") p.fornitore_id = filters.fornitore_id;
    if (filters.pagata !== "all") p.pagata = filters.pagata;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.search) p.search = filters.search;
    return p;
  }, [page, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await api.get("/transactions", buildParams());
    setItems(data.items); setTotal(data.total);
    setLoading(false);
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const [cats, cant, forn] = await Promise.all([api.get("/categories"), api.get("/cantieri"), api.get("/fornitori")]);
      setCategories(cats); setCantieri(cant); setFornitori(forn);
    })();
  }, []);

  const location = useLocation();
  useEffect(() => {
    if (location.state?.fatture) {
      setFilters((f) => ({ ...f, tipo: "uscita", cantiere_id: "__none__" }));
      setPage(0);
    }
    if (location.state?.scadute) {
      setFilters((f) => ({ ...f, tipo: "uscita", pagata: "no" }));
      setPage(0);
    }
  }, [location.state]);

  const setF = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };

  const allCats = categories ? [...categories.entrate, ...categories.uscite] : [];

  const exportCsv = async () => {
    const p = { ...buildParams() };
    delete p.skip; delete p.limit;
    try {
      await downloadCsv("/transactions/export", "transazioni.csv", p);
      toast.success("Transazioni esportate in CSV.");
    } catch { toast.error("Esportazione non riuscita."); }
  };

  const confirmDelete = async () => {
    try {
      await api.del(`/transactions/${toDelete.id}`);
      toast.success("Transazione eliminata.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const togglePagata = async (t) => {
    const next = !t.pagata;
    setItems((list) => list.map((x) => (x.id === t.id ? { ...x, pagata: next } : x)));
    try {
      await api.patch(`/transactions/${t.id}/pagata`, { pagata: next });
      toast.success(next ? "Segnata come pagata." : "Segnata come non pagata.");
    } catch (err) {
      toast.error(err.message || "Aggiornamento non riuscito.");
      setItems((list) => list.map((x) => (x.id === t.id ? { ...x, pagata: !next } : x)));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(INIT_FILTERS);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{isOwner ? "Entrate & Uscite" : "Uscite"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} transazioni registrate.</p>
        </div>
        <div className="flex gap-2">
          <ImportXmlButton onImported={() => { setPage(0); load(); }} />
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button" className="gap-2"><Download className="h-4 w-4" /> <span className="hidden sm:inline">Esporta CSV</span></Button>
          <Button onClick={() => { setEditing(null); setModal(true); }} data-testid="add-transaction-button"
            className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900"><Plus className="h-4 w-4" /> Transazione</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cerca descrizione o note..." data-testid="tx-search-input" className="pl-9"
              value={filters.search} onChange={(e) => setF("search", e.target.value)} />
          </div>
          <Select value={filters.tipo} onValueChange={(v) => setF("tipo", v)}>
            <SelectTrigger data-testid="filter-tipo-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {isOwner && <SelectItem value="entrata">Entrate</SelectItem>}
              <SelectItem value="uscita">Uscite</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.categoria_key} onValueChange={(v) => setF("categoria_key", v)}>
            <SelectTrigger data-testid="filter-categoria-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {allCats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.cantiere_id} onValueChange={(v) => setF("cantiere_id", v)}>
            <SelectTrigger data-testid="filter-cantiere-select"><SelectValue placeholder="Cantiere" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i cantieri</SelectItem>
              <SelectItem value="__none__">Senza commessa (da assegnare)</SelectItem>
              {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.fornitore_id} onValueChange={(v) => setF("fornitore_id", v)}>
            <SelectTrigger data-testid="filter-fornitore-select"><SelectValue placeholder="Fornitore" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i fornitori</SelectItem>
              {fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.pagata} onValueChange={(v) => setF("pagata", v)}>
            <SelectTrigger data-testid="filter-pagata-select"><SelectValue placeholder="Stato pagamento" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="si">Solo pagate</SelectItem>
              <SelectItem value="no">Solo non pagate</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} />
          </div>
          <div className="flex gap-2 lg:col-span-5">
            <Input type="date" data-testid="filter-date-to" className="max-w-[180px]" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} />
            {hasActiveFilters && (
              <Button variant="ghost" onClick={() => { setFilters(INIT_FILTERS); setPage(0); }} data-testid="clear-filters-button" className="gap-2 text-muted-foreground">
                <FilterX className="h-4 w-4" /> Azzera filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 rounded bg-secondary animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Nessuna transazione trovata con questi filtri.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Descrizione</TableHead><TableHead>Categoria</TableHead>
                    <TableHead>Cantiere</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((t) => (
                    <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(t.data)}</TableCell>
                      <TableCell className="font-medium max-w-[220px] truncate">
                        <span className="flex items-center gap-1.5">{t.descrizione}{t.allegato_file_id
                          ? <a href={fileUrl(t.allegato_file_id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`tx-allegato-link-${t.id}`}><Paperclip className="h-3 w-3 text-amber-600 shrink-0" /></a>
                          : (t.allegato_nome && <Paperclip className="h-3 w-3 text-amber-600 shrink-0" />)}</span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">{t.categoria_label}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{t.cantiere_nome || "Generale"}</TableCell>
                      <TableCell className={cx("text-xs whitespace-nowrap", (t.scadenza && !t.pagata && t.scadenza < todayISO()) ? "text-red-600 font-semibold" : "text-muted-foreground")} data-testid={`tx-scadenza-${t.id}`}>
                        {t.scadenza ? fmtDate(t.scadenza) : "—"}
                      </TableCell>
                      <TableCell>
                        <button onClick={() => togglePagata(t)} data-testid={`pagata-toggle-${t.id}`}
                          className={cx("text-xs font-medium px-2.5 py-1 rounded-full transition-colors",
                            t.pagata
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                              : "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300")}>
                          {t.pagata ? "Pagata" : "Non pagata"}
                        </button>
                      </TableCell>
                      <TableCell className={cx("text-right font-mono font-semibold whitespace-nowrap", t.tipo === "entrata" ? "text-emerald-600" : "text-red-600")}>
                        {t.tipo === "entrata" ? "+" : "-"}{eur(t.importo)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {isXmlAttachment(t) && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-amber-600" data-testid={`preview-tx-${t.id}`} onClick={() => setPreviewTx(t)}><Eye className="h-3.5 w-3.5" /></Button>}
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-tx-${t.id}`} onClick={() => { setEditing(t); setModal(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                          {isOwner && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-tx-${t.id}`} onClick={() => setToDelete(t)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Pagina {page + 1} di {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-button"><ChevronLeft className="h-4 w-4" /> Precedente</Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page-button">Successiva <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <TransactionModal open={modal} onOpenChange={setModal} categories={categories} cantieri={cantieri} fornitori={fornitori} initial={editing} onSaved={load} />

      <FatturaPreviewModal tx={previewTx} open={Boolean(previewTx)} onOpenChange={(o) => !o && setPreviewTx(null)} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la transazione?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.descrizione}" verrà eliminata definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-tx">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-tx" className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
